import { Button, Icon } from "@blueprintjs/core";
import { type ScheduledEvent } from "@package/ui-gql/index-data-access";
import moment from "moment-timezone";
import { BROWSER_TIMEZONE, type SchedulerEventTone } from "./calendar-utils";
import {
  describeSchedulerAction,
  type SchedulerActionDescription,
} from "./scheduler-action-language";
import { summarizeRecurrence } from "./scheduler-recurrence-utils";
import type { SchedulerEntityNames } from "./use-scheduler-entity-names";

const TONE_TILE_CLASS: Record<SchedulerEventTone, string> = {
  profile: "bg-brand-green-tint text-brand-green-dark",
  "post-enable": "bg-brand-navy/10 text-brand-navy",
  "post-disable": "bg-brand-paper text-brand-muted",
  neutral: "bg-brand-paper text-brand-muted",
};

type SchedulerEventSummaryProps = {
  event: ScheduledEvent;
  names: SchedulerEntityNames;
  onEditSeries?: () => void;
};

export function SchedulerEventSummary({
  event,
  names,
  onEditSeries,
}: SchedulerEventSummaryProps) {
  const eventZone = event.timezone || BROWSER_TIMEZONE;
  const zonesDiffer = eventZone !== BROWSER_TIMEZONE;

  const start = event.start ? moment(event.start) : null;
  const end = event.end ? moment(event.end) : null;

  const dateLabel = start
    ? end && !start.isSame(end, "day")
      ? `${start.tz(eventZone).format("ddd, MMM D")} – ${end.tz(eventZone).format("ddd, MMM D")}`
      : start.tz(eventZone).format("ddd, MMM D")
    : null;

  const timeLabel = start
    ? end
      ? `${start.tz(eventZone).format("h:mm A")} – ${end.tz(eventZone).format("h:mm A")}`
      : start.tz(eventZone).format("h:mm A")
    : null;

  const recurrenceSummary = summarizeRecurrence(
    event.recurring,
    start ? start.toDate() : new Date(),
  );

  const startActions = (event.startAction ?? [])
    .map((action) => describeSchedulerAction(action, names))
    .filter((description): description is SchedulerActionDescription =>
      Boolean(description),
    );
  const endActions = (event.endAction ?? [])
    .map((action) => describeSchedulerAction(action, names))
    .filter((description): description is SchedulerActionDescription =>
      Boolean(description),
    );

  return (
    // No width class here -- the parent (SchedulerEventPopover) already sets
    // w-80 on the card this renders inside.
    <div>
      <div className="text-sm text-brand-muted">
        {dateLabel}
        {timeLabel && <> &middot; {timeLabel}</>}
      </div>
      {zonesDiffer && (
        <div className="mt-1 text-xs text-brand-muted">
          {eventZone}
          {start && (
            <>
              {" "}
              &middot; {start.tz(BROWSER_TIMEZONE).format("h:mm A")} your time
            </>
          )}
        </div>
      )}
      {recurrenceSummary && (
        <div className="mt-1 flex items-center gap-1 text-xs text-brand-muted">
          <Icon icon="repeat" size={11} />
          {recurrenceSummary}
        </div>
      )}

      {startActions.length > 0 && (
        <SchedulerActionSection actions={startActions} title="When it starts" />
      )}
      {endActions.length > 0 && (
        <SchedulerActionSection actions={endActions} title="When it ends" />
      )}

      {event.groupId && onEditSeries && (
        <div className="mt-3 flex items-center justify-between border-t border-brand-line pt-2 text-xs">
          <span className="text-brand-muted">Part of a recurring series</span>
          <Button
            minimal
            onClick={onEditSeries}
            rightIcon="chevron-right"
            small
            text="Edit series"
          />
        </div>
      )}
    </div>
  );
}

function SchedulerActionSection({
  actions,
  title,
}: {
  actions: SchedulerActionDescription[];
  title: string;
}) {
  return (
    <div className="mt-3">
      <p className="text-[11px] font-semibold tracking-wide text-brand-muted uppercase">
        {title}
      </p>
      <div className="mt-1.5 flex flex-col gap-1.5">
        {actions.map((action, index) => (
          <div className="flex items-center gap-2" key={index}>
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${TONE_TILE_CLASS[action.tone]}`}
            >
              <Icon icon={action.icon} size={12} />
            </span>
            <span className="text-sm text-brand-ink">{action.sentence}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
